/**
 * InkBleedText — text fade-in with optional Pentiment "wet ink" register.
 *
 * Original implementation used Motion `staggerChildren` to animate every
 * single character — for a typical 100-character question prompt that
 * meant ~100 motion.span elements all running independent animation
 * loops. On weak GPUs (Chromebooks, mid-range phones) this caused
 * noticeable FPS drops the entire time the combat modal was open.
 *
 * Replaced with a single CSS keyframe fade-in on the whole string.
 * Same perceptual "text is settling in" impression — the whole prompt
 * gently fades and translates into place — at ~1% of the reconciliation
 * cost. The per-letter wet→dry effect was also barely visible at the
 * font sizes we ship (16-18px); the savings are essentially free.
 *
 * API preserved so the rest of the codebase doesn't change. `stagger`
 * and `duration` props now control the overall fade duration; per-
 * letter staggering is no longer applied.
 *
 * Honours `prefers-reduced-motion`: skips the fade entirely and renders
 * static text with no animation cost.
 */

import { type CSSProperties } from 'react';
import { surfaces, fontStacks } from '@qd/design-tokens';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface InkBleedTextProps {
  /** The text to reveal. */
  text: string;
  /** Legacy: was per-letter stagger; ignored in the simplified impl. */
  stagger?: number;
  /** Delay before the fade begins, in seconds. */
  delay?: number;
  /** Total fade-in duration, in seconds. Default 0.4. */
  duration?: number;
  /** Text colour. Default ink. */
  color?: string;
  /** Inline style override merged onto the wrapping span. */
  style?: CSSProperties;
  /** Force-disable the animation (e.g. when re-mounting the same prompt). */
  disabled?: boolean;
}

const KEYFRAMES_ID = 'qd-inkbleed-fade-in';

export function InkBleedText({
  text,
  delay = 0,
  duration = 0.4,
  color = surfaces.ink,
  style,
  disabled = false,
}: InkBleedTextProps) {
  const reduced = useReducedMotion();
  const skip = reduced || disabled;

  if (skip) {
    return (
      <span
        style={{
          fontFamily: fontStacks.body,
          color,
          ...style,
        }}
      >
        {text}
      </span>
    );
  }

  return (
    <>
      <style>{`
        @keyframes ${KEYFRAMES_ID} {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <span
        style={{
          fontFamily: fontStacks.body,
          color,
          display: 'inline-block',
          opacity: 0,
          willChange: 'opacity, transform',
          animation: `${KEYFRAMES_ID} ${duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}s forwards`,
          ...style,
        }}
      >
        {text}
      </span>
    </>
  );
}
